import { buildRoleAwarePrefix } from '@/utils/api';
import {
  coerceFiniteNumber,
  coerceNonEmptyString,
  extractListFromPayload,
  isRecord,
} from '@/utils/apiPayload';
import { MimeType } from '@/utils/fileValidation';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';
import type { UserExportPayload } from '@/utils/userExport';
import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Estate } from '../models/AppViewModel';
import { RecordResponse } from '../models/ServiceTypes';
import { advisorInvestorsApi } from './advisorInvestorsApi';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';
import { hasUserProfile, isAdvisorUserRole } from '@/constants/roles';
import type { UserTag } from './userTag';

function isSpreadsheetContentType(contentType: string): boolean {
  const lower = contentType.toLowerCase();
  return (
    lower.includes('excel') ||
    lower.includes('spreadsheet') ||
    lower.includes('csv') ||
    lower.includes('octet-stream')
  );
}

function abortedFetchError(): FetchBaseQueryError {
  return {
    status: 'CUSTOM_ERROR',
    error: 'ExportAborted',
  };
}

const exportAbortSignals = new Map<string, AbortSignal>();

export function bindExportAbortSignal(signal: AbortSignal): string {
  const id = crypto.randomUUID();
  exportAbortSignals.set(id, signal);
  return id;
}

export function unbindExportAbortSignal(id: string): void {
  exportAbortSignals.delete(id);
}

type SpreadsheetBody = {
  size: number;
  arrayBuffer: () => Promise<ArrayBuffer>;
};

function isSpreadsheetBody(value: object): value is SpreadsheetBody {
  return (
    'size' in value &&
    typeof value.size === 'number' &&
    'arrayBuffer' in value &&
    typeof value.arrayBuffer === 'function'
  );
}

async function toSameRealmBlob(body: SpreadsheetBody): Promise<Blob> {
  if (body instanceof Blob) {
    return body;
  }
  const type = 'type' in body && typeof body.type === 'string' ? body.type : '';
  return new Blob([await body.arrayBuffer()], { type });
}

async function handleExportBlobResponse(response: Response) {
  if (response.ok) {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.length === 0 || isSpreadsheetContentType(contentType)) {
      return response.blob();
    }
    return response.text();
  }
  return response.text();
}

export {
  USER_TAG_ASSIGN_RIGHT,
  USER_TAG_DELETE_RIGHT,
  USER_TAG_EDIT_RIGHT,
  userTagCanAssign,
  userTagCanDelete,
  userTagCanEdit,
  type UserTag,
} from './userTag';

// TODO: Set up env variables for api URLs
// Add query api for each role type
// Create different slices for the various types of data these endpoints bring back
// ie LayoutSlice for menu items, UserProfile slice for stuff like name, email, etc
// ^^ careful to not lose logged in user when impersonating though

// example:
// https://service.wealthaccess.com//api/Administrator/users?searchTerm=david%20benskin&page=1&sort%5B0%5D.Property=FullName&sort%5B0%5D.Direction=asc&sort%5B0%5D.Order=0&_=1702013552576

export interface UserModel {
  UserId: number;
  FullName?: string | undefined;
  Name?: string | undefined;
  UserName?: string | undefined;
  Roles?: string[] | undefined;
  IsLockedOut: boolean;
  IsAdminLocked: boolean;
  IsConfirmed: boolean;
  FirstName?: string | undefined;
  LastName?: string | undefined;
}

export interface InvestorDetail {
  InvestorId?: number | null;
  ByAllId?: string | null;
  PrimaryAdvisorId?: number | null;
  PrimaryAdvisorName?: string | null;
  YodleeUserName?: string | null;
}

export interface AdvisorDetail {
  AdvisorId?: number | null;
  ByAllId?: string | null;
}

export interface UserDetail {
  UserId?: number | null;
  FirmId?: number | null;
  FirmName?: string | null;
  AdvisorGroupId?: number | null;
  AdvisorGroupName?: string | null;
  Investor?: InvestorDetail | null;
  Estates?: Estate[] | null;
  Advisor?: AdvisorDetail | null;
}

export interface AdvisorInvestor {
  CanMGPSSO: boolean;
  InvestorId: number;
  Name: string;
  NetWorth: number;
  TotalAssets: number;
  TotalLiabilities: number;
  Home: number;
  HomePercentage?: number | null;
  LastLogin?: string | null;
  LastOrionLogin?: string | null;
  OnboardingStep: number;
  OnboardingStepDisplay: string;
  CanInvite: boolean;
  Groups?: AdvisorInvestorGroup[] | null;
  CanImpersonate: boolean;
  CanDelete: boolean;
  InHousehold: boolean;
  LastInviteDate?: string | null;
  UserId: number;
  UserName?: string;
  PrimaryAdvisorId?: number | null;
  PrimaryAdvisorName?: string | null;
  IsAdminLocked: boolean;
  IsSensitiveUser: boolean;
}

export interface AdvisorInvestorGroup {
  Id?: number | string | null;
  ParentId?: number | string | null;
  Name?: string | null;
}

export interface InvestorActivity {
  AuditId: number;
  AuditDate: string;
  Name: string;
  Action: string;
  InvestorId: number;
}

// --- Advisor: Invite History ---
export interface InviteHistoryRecord {
  InviteDateTime: string;
  AdvisorName: string;
}

// --- Investor User Profile ---
export interface UserDependentProfile {
  UserDependentId?: number | null;
  BirthDay?: string | null; // ISO or MM/DD/YYYY accepted by API
}

export interface UserProfile {
  UserProfileId: number;
  UserId: number;
  CanEditNameAndEmail: boolean;
  FirstName: string;
  LastName: string;
  EmailAddress: string;
  TaxId?: string | null;
  SpouseTaxId?: string | null;
  ApproximateNetWorth?: number | null;
  PhoneNumber?: string | null;
  EmploymentStatus?: number | null; // 1=Full-Time, 2=Part-Time, 3=Unemployed, 4=Retired
  EmploymentStatusDisplay?: string | null;
  BusinessTitle?: string | null;
  Employer?: string | null; // optional - returned by some endpoints
  Gender?: number | null; // 1=Male, 2=Female, 3=Other/Not Specified
  MarriageStatus?: number | null; // 1=Single, 2=Married, ... see UI mapping
  MarriageStatusDisplay?: string | null;
  Birthday?: string | null;
  SpouseFirstName?: string | null;
  SpouseLastName?: string | null;
  SpouseBirthday?: string | null;
  Dependents?: UserDependentProfile[];
  Address?: string | null;
  AddressLine1?: string | null;
  City?: string | null;
  State?: string | null; // e.g., "TN"
  Zip?: string | null;
  InvestorId?: number | null;
}

// --- Advisor: Investor Setup (Edit User Information in flyout) ---
export interface AdvisorInvestorSetup {
  InvestorId: number;
  UserId: number;
  FirstName: string;
  LastName: string | null;
  Name: string;
  UserName: string;
  LastLogin?: string | null;
  OnboardingStep: number;
  OnboardingStepDisplay: string;
  CanInvite: boolean;
  CanImpersonate: boolean;
  CanDelete: boolean;
  CanAssign: boolean;
  IsAdminLocked: boolean;
  // Primary advisor info (optional; present in some payloads)
  PrimaryAdvisorId?: number | null;
  PrimaryAdvisorName?: string | null;
  // Optional editable fields
  MaritalStatus?: number | null; // 1=Single, 2=Married, 3=Divorced, 4=Separated, 5=Widowed, 6=Domestic Partner
  DOB?: string | null; // ISO or MM/DD/YYYY
  SocialSecurityNumber?: string | null;
  State?: string | null; // e.g., "AK"
  Gender?: number | null; // 1=Male, 2=Female, 3=Other/Not Specified
  TDAceUserId?: string | null;
  ExternalId?: string | null;
}

export type AdvisorInvestorSetupUpdate = Partial<AdvisorInvestorSetup>;

/** Reads tag id from common API shapes (snake_case, camelCase, PascalCase). */
function readUserTagIdFromRow(r: Record<string, unknown>): number | null {
  return (
    coerceFiniteNumber(r.InvestorTagId) ??
    coerceFiniteNumber(r.investorTagId) ??
    coerceFiniteNumber(r.tag_id) ??
    coerceFiniteNumber(r.tagId) ??
    coerceFiniteNumber(r.TagId) ??
    coerceFiniteNumber(r.tagID) ??
    coerceFiniteNumber(r.TagID) ??
    coerceFiniteNumber(r.UserTagId) ??
    coerceFiniteNumber(r.userTagId) ??
    null
  );
}

function readUserTagNameFromRow(r: Record<string, unknown>): string | null {
  return coerceNonEmptyString(r.name ?? r.Name ?? r.tagName ?? r.TagName);
}

function readUserTagRightsFromRow(r: Record<string, unknown>): string[] {
  const rights = r.rights ?? r.Rights;
  if (rights == null) return [];
  if (!Array.isArray(rights)) return [];
  return rights.filter((x): x is string => typeof x === 'string');
}

/** Normalizes the GET /api/user/tags/{id}/accounts payload (a raw int array) to number[]. */
export function normalizeUserTagAccountIds(data: unknown): number[] {
  if (!Array.isArray(data)) return [];
  const out: number[] = [];
  for (const v of data) {
    const n = coerceFiniteNumber(v);
    if (n != null) {
      out.push(n);
    }
  }
  return out;
}

function normalizeOneUserTagRow(row: unknown): UserTag | null {
  if (!isRecord(row)) return null;
  const tagId = readUserTagIdFromRow(row);
  const name = readUserTagNameFromRow(row);
  if (tagId == null || name == null) return null;
  return {
    tagId,
    name,
    rights: readUserTagRightsFromRow(row),
  };
}

/** Normalizes GET /api/user/tags payloads (array, paged Records, or tag lists with varied casing). */
export function normalizeUserTagsResponse(data: unknown): UserTag[] {
  const rows = extractListFromPayload(data);
  const out: UserTag[] = [];
  for (const row of rows) {
    const tag = normalizeOneUserTagRow(row);
    if (tag) {
      out.push(tag);
    }
  }
  return out;
}

/** An advisor's profile hangs off their own user; an investor's off the investor. */
const userProfileUrl = (currentRole?: string | null): string =>
  isAdvisorUserRole(currentRole)
    ? '/api/advisor/userProfile'
    : '/api/investor/userProfile';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: waServicesQuery,
  tagTypes: ['AdvisorInvestorSetup', 'AdvisorInvestors', 'UserTags'],
  endpoints: (builder) => ({
    getAdminUsers: builder.query<RecordResponse<UserModel>, string>({
      query: (query) => ({
        url: `/api/Administrator/users`,
        method: 'GET',
        params: {
          searchTerm: query,
        },
      }),
    }),
    // Prospect: leave the setup wizard for the full client portal.
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- RTK Query uses void for no-argument endpoints
    completeProspectSetup: builder.mutation<boolean, void>({
      query: () => ({
        url: '/api/investor/ChangeView',
        method: 'PUT',
      }),
    }),
    // Advisor: Fetch investor user profile by investorId (for export)
    getAdvisorInvestorUserProfile: builder.query<UserProfile, number>({
      queryFn: async (investorId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investors/${String(investorId)}/userProfile`,
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as UserProfile };
      },
    }),
    updateAdvisorInvestorUserProfile: builder.mutation<
      UserProfile,
      { investorId: number; body: Partial<UserProfile> }
    >({
      queryFn: async ({ investorId, body }, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/advisor/investors/${String(investorId)}/userProfile`,
          method: 'PUT',
          body,
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as UserProfile };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdvisorInvestors', id: `INV_${String(arg.investorId)}` },
      ],
    }),
    // Advisor: Invite history for investor
    getAdvisorInvestorInviteHistory: builder.query<
      RecordResponse<InviteHistoryRecord>,
      number
    >({
      queryFn: async (investorId, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const url = `${buildRoleAwarePrefix(currentRole, investorId)}/inviteHistory`;
        const result = await baseQuery({
          url,
          method: 'GET',
          params: { _: Date.now().toString() },
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as RecordResponse<InviteHistoryRecord> };
      },
    }),
    // Fetch a single advisor investor by id
    getAdvisorInvestorById: builder.query<AdvisorInvestor, number>({
      query: (investorId: number) => ({
        url: `/api/advisor/investors/${String(investorId)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, investorId) => [
        { type: 'AdvisorInvestors', id: `INV_${String(investorId)}` },
      ],
    }),
    getUserDetails: builder.query<UserDetail, number>({
      query: (userId: number) => ({
        url: `/api/Administrator/userDetails/${String(userId)}`,
        method: 'GET',
      }),
    }),
    getAdvisorInvestors: builder.query<
      RecordResponse<AdvisorInvestor>,
      { searchTerm?: string; page?: number }
    >({
      query: ({ searchTerm, page = 1 }) => ({
        url: `/api/advisor/investors`,
        method: 'GET',
        params: {
          ...(searchTerm && { searchTerm }),
          page,
          'sort[0].Property': 'NetWorth',
          'sort[0].Direction': 'desc',
          'sort[0].Order': 1,
        },
      }),
      providesTags: (result) => {
        const base = [{ type: 'AdvisorInvestors' as const, id: 'LIST' }];
        if (!result?.Records) return base;
        return [
          ...base,
          ...result.Records.map((r) => ({
            type: 'AdvisorInvestors' as const,
            id: r.UserId,
          })),
          ...result.Records.map((r) => ({
            type: 'AdvisorInvestors' as const,
            id: `INV_${String(r.InvestorId)}`,
          })),
        ];
      },
    }),
    getInvestorActivity: builder.query<
      RecordResponse<InvestorActivity>,
      { limit?: number; investorId?: number }
    >({
      query: ({ limit, investorId }) => ({
        url: `/api/Advisor/InvestorActivity`,
        method: 'GET',
        params: {
          ...(limit ? { limit } : {}),
          ...(typeof investorId === 'number' ? { investorId } : {}),
          'sort[0].Property': 'AuditDate',
          'sort[0].Direction': 'desc',
          'sort[0].Order': 0,
        },
      }),
    }),
    getUserTags: builder.query<UserTag[], undefined>({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/user/tags`,
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error };
        }

        const normalized = normalizeUserTagsResponse(result.data);
        return { data: normalized };
      },
      providesTags: [{ type: 'UserTags', id: 'LIST' }],
    }),
    getUserTagAccounts: builder.query<number[], number>({
      queryFn: async (tagId, _api, _extra, baseQuery) => {
        const result = await baseQuery({
          url: `/api/user/tags/${String(tagId)}/accounts`,
          method: 'GET',
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: normalizeUserTagAccountIds(result.data) };
      },
      providesTags: (_result, _error, tagId) => [
        { type: 'UserTags', id: `ACCOUNTS-${String(tagId)}` },
      ],
    }),
    createUserTag: builder.mutation<
      number,
      { name: string; wealthAccountIds: number[] }
    >({
      query: ({ name, wealthAccountIds }) => ({
        url: '/api/user/tags',
        method: 'POST',
        body: { Name: name, WealthAccountIds: wealthAccountIds },
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: 'UserTags', id: 'LIST' }],
    }),
    updateUserTag: builder.mutation<
      boolean,
      { tagId: number; name: string; wealthAccountIds: number[] }
    >({
      query: ({ tagId, name, wealthAccountIds }) => ({
        url: `/api/user/tags/${String(tagId)}`,
        method: 'PUT',
        body: { Name: name, WealthAccountIds: wealthAccountIds },
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              { type: 'UserTags', id: 'LIST' },
              { type: 'UserTags', id: `ACCOUNTS-${String(arg.tagId)}` },
            ],
    }),
    deleteUserTag: builder.mutation<boolean, number>({
      query: (tagId) => ({
        url: `/api/user/tags/${String(tagId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, tagId) =>
        error
          ? []
          : [
              { type: 'UserTags', id: 'LIST' },
              { type: 'UserTags', id: `ACCOUNTS-${String(tagId)}` },
            ],
    }),
    // The signed-in user's own profile. An advisor has no investor relationship,
    // so the investor route 500s for them and they get their own.
    getUserProfile: builder.query<UserProfile, undefined>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        if (!hasUserProfile(state.auth.currentRole)) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'The current role has no user profile.',
            },
          };
        }
        const result = await baseQuery({
          url: userProfileUrl(state.auth.currentRole),
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as UserProfile };
      },
    }),
    updateUserProfile: builder.mutation<UserProfile, Partial<UserProfile>>({
      queryFn: async (body, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const result = await baseQuery({
          url: userProfileUrl(state.auth.currentRole),
          method: 'PUT',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as UserProfile };
      },
    }),
    // Advisor: Get investor setup details by userId
    getAdvisorInvestorSetup: builder.query<AdvisorInvestorSetup, number>({
      query: (userId: number) => ({
        url: `/api/advisor/setup/investors/${String(userId)}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, userId) => [
        { type: 'AdvisorInvestorSetup', id: userId },
      ],
    }),
    // Advisor: Update investor setup details by userId
    updateAdvisorInvestorSetup: builder.mutation<
      AdvisorInvestorSetup,
      { userId: number; investorId?: number; body: AdvisorInvestorSetupUpdate }
    >({
      query: ({ userId, body }) => ({
        url: `/api/advisor/setup/investors/${String(userId)}`,
        method: 'PUT',
        body: {
          ...body,
          UserId: userId,
        },
      }),
      invalidatesTags: (result, _error, arg) => {
        // The client details flyout is keyed on the `INV_<investorId>` tag, so
        // it only refetches when that exact tag is invalidated. Prefer the
        // explicit arg, then the echoed response, then the body.
        const investorId =
          arg.investorId ?? result?.InvestorId ?? arg.body.InvestorId;
        return [
          { type: 'AdvisorInvestorSetup', id: arg.userId },
          { type: 'AdvisorInvestors', id: arg.userId },
          { type: 'AdvisorInvestors', id: 'LIST' },
          ...(investorId
            ? [
                {
                  type: 'AdvisorInvestors' as const,
                  id: `INV_${String(investorId)}`,
                },
              ]
            : []),
        ];
      },
      // The Clients.tsx grids are served by advisorInvestorsApi, a separate
      // slice, so the tags above can't reach them. Once the update succeeds,
      // mirror the invalidation across slices so the grid re-fetches the
      // edited client's row and stays in sync with the flyout.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // Update failed; nothing to refresh.
          return;
        }
        type InvalidateAdvisorTagsArg = Parameters<
          typeof advisorInvestorsApi.util.invalidateTags
        >[0];
        dispatch(
          advisorInvestorsApi.util.invalidateTags([
            { type: 'AdvisorInvestorsList', id: 'LIST' },
            { type: 'AdvisorSetupInvestors', id: 'LIST' },
            // Change username in the prospect invite edits a prospect too.
            { type: 'AdvisorProspects', id: 'LIST' },
          ] as InvalidateAdvisorTagsArg),
        );
      },
    }),
    // Advisor: Delete investor/user by userId
    deleteAdvisorInvestor: builder.mutation<
      { success: boolean } | undefined,
      { userId: number; investorId?: number }
    >({
      query: ({ userId }) => ({
        url: `/api/advisor/setup/investors/${String(userId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AdvisorInvestorSetup', id: arg.userId },
        { type: 'AdvisorInvestors', id: arg.userId },
        { type: 'AdvisorInvestors', id: 'LIST' },
        ...(arg.investorId
          ? [
              {
                type: 'AdvisorInvestors' as const,
                id: `INV_${String(arg.investorId)}`,
              },
            ]
          : []),
      ],
      // The Clients.tsx grids are served by advisorInvestorsApi, a separate
      // slice, so the tags above can't reach them. Mirror the same
      // cross-slice invalidation the update mutation above performs, so a
      // deleted client stops appearing in the grid immediately instead of
      // waiting for cache expiry.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          // Delete failed; nothing to refresh.
          return;
        }
        type InvalidateAdvisorTagsArg = Parameters<
          typeof advisorInvestorsApi.util.invalidateTags
        >[0];
        dispatch(
          advisorInvestorsApi.util.invalidateTags([
            { type: 'AdvisorInvestorsList', id: 'LIST' },
            { type: 'AdvisorSetupInvestors', id: 'LIST' },
          ] as InvalidateAdvisorTagsArg),
        );
      },
    }),
    // Advisor: Send password reset link to user's email by userId
    sendPasswordResetEmail: builder.mutation<
      { success?: boolean } | undefined,
      { userId: number; body: Partial<AdvisorInvestor> }
    >({
      query: ({ userId, body }) => ({
        url: `/api/advisor/setup/investors/${String(userId)}/sendPasswordResetEmail`,
        method: 'PUT',
        body,
      }),
    }),
    exportUserData: builder.mutation<
      { success: true; fileName: string },
      UserExportPayload & { abortId?: string }
    >({
      queryFn: async (arg, api, _extraOptions, baseQuery) => {
        const { abortId, ...payload } = arg;
        const exportSignal =
          abortId !== undefined ? exportAbortSignals.get(abortId) : undefined;

        if (abortId !== undefined && exportSignal === undefined) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Export abort signal was not registered',
            } as FetchBaseQueryError,
          };
        }

        const forwardAbort = () => {
          api.abort();
        };
        if (exportSignal) {
          exportSignal.addEventListener('abort', forwardAbort);
          if (exportSignal.aborted) {
            exportSignal.removeEventListener('abort', forwardAbort);
            return { error: abortedFetchError() };
          }
        }

        try {
          const result = await baseQuery({
            url: '/api/user/export',
            method: 'POST',
            body: payload,
            responseHandler: handleExportBlobResponse,
          });

          const aborted = () =>
            api.signal.aborted || exportSignal?.aborted === true;

          if (result.error) {
            if (aborted()) {
              return { error: abortedFetchError() };
            }
            return { error: result.error };
          }

          const body = result.data;
          if (
            typeof body !== 'object' ||
            body === null ||
            !isSpreadsheetBody(body)
          ) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error:
                  typeof body === 'string' && body.length > 0
                    ? body
                    : 'Export response was not a spreadsheet',
                data: body,
              } as FetchBaseQueryError,
            };
          }

          if (body.size === 0) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'Export response was empty',
              } as FetchBaseQueryError,
            };
          }

          if (aborted()) {
            return { error: abortedFetchError() };
          }

          exportSignal?.removeEventListener('abort', forwardAbort);

          const fileName = `${payload.FileName.replace(/\.xl[st]x?$/i, '')}.xls`;
          try {
            await triggerBlobDownload(
              await toSameRealmBlob(body),
              fileName,
              MimeType.Xls,
            );
          } catch (err) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: err instanceof Error ? err.message : String(err),
              } as FetchBaseQueryError,
            };
          }

          return { data: { success: true, fileName } };
        } finally {
          exportSignal?.removeEventListener('abort', forwardAbort);
        }
      },
    }),
  }),
});

export const {
  useGetUserTagsQuery,
  useGetUserTagAccountsQuery,
  useCreateUserTagMutation,
  useUpdateUserTagMutation,
  useDeleteUserTagMutation,
  useGetAdminUsersQuery,
  useGetUserDetailsQuery,
  useGetAdvisorInvestorsQuery,
  useGetAdvisorInvestorByIdQuery,
  useLazyGetAdvisorInvestorByIdQuery,
  useGetInvestorActivityQuery,
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useGetAdvisorInvestorUserProfileQuery,
  useLazyGetAdvisorInvestorUserProfileQuery,
  useUpdateAdvisorInvestorUserProfileMutation,
  useGetAdvisorInvestorSetupQuery,
  useUpdateAdvisorInvestorSetupMutation,
  useDeleteAdvisorInvestorMutation,
  useSendPasswordResetEmailMutation,
  useGetAdvisorInvestorInviteHistoryQuery,
  useCompleteProspectSetupMutation,
} = usersApi;
